import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, Send, Check, Loader2 } from 'lucide-react';
import { API } from '../api/client';

const TOPICS = [
  { value: 'partnership',   label: 'Partnership / affiliate' },
  { value: 'listing',       label: 'Listing correction' },
  { value: 'submission',    label: 'Paid submission support' },
  { value: 'press',         label: 'Press / media' },
  { value: 'bug',           label: 'Bug report' },
  { value: 'feedback',      label: 'Feedback / feature idea' },
  { value: 'other',         label: 'Something else' },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', topic: 'other', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        origin_url: typeof window !== 'undefined' ? window.location.href : '',
      });
      setSent(true);
      setForm({ name: '', email: '', topic: 'other', message: '' });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not send. Please try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-container" data-testid="contact-page">
      <div className="crumbs">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>Contact</span>
      </div>

      <div className="contact-hero">
        <div className="contact-hero-icon" aria-hidden><Mail size={22} /></div>
        <div>
          <h1 className="page-title" style={{ marginBottom: 4 }}>Get in touch</h1>
          <p className="muted">
            Partnerships, listings, press, or just to say hi. We read every message.
          </p>
        </div>
      </div>

      <div className="contact-grid">
        <form className="contact-form" onSubmit={submit} data-testid="contact-form">
          {sent ? (
            <div className="contact-success" data-testid="contact-success">
              <div className="success-check"><Check size={20} /></div>
              <h3>Message sent</h3>
              <p className="muted">Thanks — we usually reply within 1&ndash;2 business days.</p>
              <button type="button" className="pill-btn" onClick={() => setSent(false)} data-testid="contact-send-another">
                Send another
              </button>
            </div>
          ) : (
            <>
              <div className="form-row">
                <label>
                  <span>Your name</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Ada Lovelace"
                    data-testid="contact-name"
                  />
                </label>
                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="you@example.com"
                    data-testid="contact-email"
                  />
                </label>
              </div>

              <label>
                <span>Topic</span>
                <select
                  value={form.topic}
                  onChange={(e) => update('topic', e.target.value)}
                  data-testid="contact-topic"
                >
                  {TOPICS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Message</span>
                <textarea
                  required
                  rows={7}
                  minLength={10}
                  maxLength={4000}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder="Tell us what you need. Links help — feel free to paste them."
                  data-testid="contact-message"
                />
              </label>

              {err && <div className="form-error" data-testid="contact-error">{err}</div>}

              <div className="form-actions">
                <button
                  type="submit"
                  className="pill-btn primary lg"
                  disabled={busy}
                  data-testid="contact-submit"
                >
                  {busy ? <><Loader2 size={14} className="spin" /> Sending…</> : <>Send message <Send size={12} /></>}
                </button>
              </div>
            </>
          )}
        </form>

        <aside className="contact-side">
          <div className="side-block">
            <h4>Direct email</h4>
            <a href="mailto:hello@ai-framework.io" className="side-link" data-testid="contact-direct-email">
              hello@ai-framework.io
            </a>
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              Reply within 1&ndash;2 business days.
            </p>
          </div>

          <div className="side-block">
            <h4>Paid submission support</h4>
            <p className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
              Featured or Sponsored tier and need help? Pick <strong>Paid submission support</strong> in the form
              and include your Stripe receipt or submission ID.
            </p>
            <Link to="/submit" className="pill-btn ghost" style={{ fontSize: 12 }}>See submission tiers</Link>
          </div>

          <div className="side-block">
            <h4>Fix a listing</h4>
            <p className="muted" style={{ fontSize: 13 }}>
              Own a tool listed here and something is wrong? Pick <strong>Listing correction</strong> and link
              the page. We update within 24h.
            </p>
          </div>

          <div className="side-block">
            <h4>Press &amp; partnerships</h4>
            <p className="muted" style={{ fontSize: 13 }}>
              We license our taxonomy and syndicate lists to publishers. Tell us about your audience
              and we will send a media kit.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
